import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, ChevronLeft, ChevronRight, RotateCcw, Eye } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { ghost, primary, field, muted, card, rowBorder, hm, errorText, addDays, weekLabel } from './styles';
import { StatusBadge, Message, EntryDays } from './ui';

const mondayOf = (key) => { const d = new Date(`${key}T00:00:00Z`); return addDays(key, -((d.getUTCDay() + 6) % 7)); };
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// Approve / reject (or, for admin/HR, reopen) one timesheet. `mode` is the
// action that needs a reason ('reject' | 'reopen'), or null.
const Decision = ({ sheetId, canApprove, canReopen, onDone }) => {
    const [detail, setDetail] = useState(null);
    const [mode, setMode] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const toggleDetail = async () => {
        if (detail) { setDetail(null); return; }
        try { setDetail((await api.get(`/time/timesheets/${sheetId}`)).data); }
        catch (e) { onDone(false, errorText(e, 'Failed to load the timesheet')); }
    };
    const send = async (action, body = {}) => {
        setBusy(true);
        try {
            await api.post(`/time/timesheets/${sheetId}/${action}`, body);
            const text = { approve: 'Timesheet approved', reject: 'Timesheet returned for changes', reopen: 'Timesheet reopened' }[action];
            onDone(true, text);
        } catch (e) {
            onDone(false, errorText(e, 'Something went wrong'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button style={ghost} onClick={toggleDetail}><Eye size={13} /> {detail ? 'Hide entries' : 'View entries'}</button>
                {canApprove && !mode && <button style={ghost} onClick={() => setMode('reject')} disabled={busy}><X size={13} /> Reject</button>}
                {canApprove && !mode && <button className="btn btn-primary" style={primary} onClick={() => send('approve')} disabled={busy}><Check size={14} /> Approve</button>}
                {canReopen && !mode && <button style={ghost} onClick={() => setMode('reopen')} disabled={busy}><RotateCcw size={13} /> Reopen</button>}
            </div>
            {mode && (
                <form style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }} onSubmit={e => { e.preventDefault(); send(mode, { reason }); }}>
                    <input aria-label={mode === 'reject' ? 'Reason for returning' : 'Reason for reopening'} autoFocus style={{ ...field, flex: '1 1 260px' }} value={reason} onChange={e => setReason(e.target.value)}
                        placeholder={mode === 'reject' ? 'What needs to change? (sent to the employee)' : 'Why is this approved week being reopened?'} />
                    <button type="button" style={ghost} onClick={() => { setMode(null); setReason(''); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" style={primary} disabled={!reason.trim() || busy}>{mode === 'reject' ? 'Return for changes' : 'Reopen week'}</button>
                </form>
            )}
            {detail && (
                <div style={{ marginTop: 12 }}>
                    {detail.entries.length === 0 ? <p style={muted}>No entries.</p> : <EntryDays entries={detail.entries.filter(e => e.endedAt)} />}
                </div>
            )}
        </div>
    );
};

const Approvals = () => {
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = ['ADMIN', 'HR'].includes(me.role);
    const [pending, setPending] = useState([]);
    const [week, setWeek] = useState(() => addDays(mondayOf(todayKey()), -7)); // last week: the usual one to chase
    const [team, setTeam] = useState(null);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        try {
            const [p, t] = await Promise.all([api.get('/time/timesheets/pending'), api.get(`/time/timesheets/team?week=${week}`)]);
            setPending(p.data); setTeam(t.data);
        } catch (e) {
            setMessage({ ok: false, text: errorText(e, 'Failed to load approvals') });
        }
    }, [week]);

    useEffect(() => { load(); }, [load]);

    const done = (ok, text) => { setMessage({ ok, text }); if (ok) load(); };
    const currentWeek = mondayOf(todayKey());
    const counts = team ? team.people.reduce((c, p) => ({ ...c, [p.status]: (c[p.status] || 0) + 1 }), {}) : {};

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1000 }}>
            <PageHeader crumbs={crumbsFor('timesheetApprovals')} backTo="/time" backLabel="Time Tracker" />
            <h1 style={{ marginBottom: 4 }}>Timesheet Approvals</h1>
            <p style={{ ...muted, fontSize: 15, marginTop: 0 }}>{isAdmin ? 'Weekly timesheets from everyone in the organisation.' : 'Weekly timesheets from the people who report to you, directly or further down.'}</p>
            <Message message={message} />

            <section className="card" style={card}>
                <h3 style={{ marginTop: 0 }}>Awaiting your approval {pending.length > 0 && <span style={{ ...muted, fontSize: 15 }}>({pending.length})</span>}</h3>
                {pending.length === 0 && <p style={muted}>Nothing to approve right now.</p>}
                {pending.map(s => (
                    <div key={s.id} data-testid="pending-sheet" style={{ borderTop: rowBorder, padding: '12px 0', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <div style={{ flex: '1 1 260px' }}>
                            <strong>{s.user.name}</strong>
                            <div style={muted}>{weekLabel(s.weekStart)} · submitted {new Date(s.submittedAt).toLocaleDateString()}</div>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 120 }}>
                            <strong style={{ fontSize: 16 }}>{hm(s.totalSec)}</strong>
                            <div style={muted}>billable {hm(s.billableSec)}</div>
                        </div>
                        <Decision sheetId={s.id} canApprove onDone={done} />
                    </div>
                ))}
            </section>

            <section className="card" style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Team week</h3>
                    <button aria-label="Previous week" style={ghost} onClick={() => setWeek(addDays(week, -7))}><ChevronLeft size={14} /></button>
                    <strong>{weekLabel(week)}</strong>
                    <button aria-label="Next week" style={ghost} onClick={() => setWeek(addDays(week, 7))} disabled={week >= currentWeek}><ChevronRight size={14} /></button>
                    <span style={{ flex: 1 }} />
                    {team && <span style={muted}>{['APPROVED', 'SUBMITTED', 'REJECTED', 'DRAFT'].filter(k => counts[k]).map(k => `${counts[k]} ${{ APPROVED: 'approved', SUBMITTED: 'awaiting', REJECTED: 'returned', DRAFT: 'not submitted' }[k]}`).join(' · ')}</span>}
                </div>
                {team && team.people.length === 0 && <p style={muted}>Nobody reports to you.</p>}
                {team && team.people.map(p => (
                    <div key={p.user.id} data-testid="team-row" style={{ borderTop: rowBorder, padding: '10px 0', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <strong style={{ flex: '1 1 200px' }}>{p.user.name}</strong>
                        <StatusBadge status={p.status} />
                        <span style={{ minWidth: 150, textAlign: 'right' }}><strong>{hm(p.totalSec)}</strong> <span style={muted}>({hm(p.billableSec)} billable)</span></span>
                        {p.id && p.status !== 'DRAFT' && (
                            <Decision sheetId={p.id} canApprove={p.status === 'SUBMITTED'} canReopen={isAdmin && p.status === 'APPROVED'} onDone={done} />
                        )}
                    </div>
                ))}
                {team && team.people.length > 0 && <p style={{ ...muted, marginBottom: 0 }}>{team.people.length === 1 ? '1 person' : `${team.people.length} people`} · weeks are approved by the person’s manager or anyone above them; admin and HR can approve anyone.</p>}
            </section>
        </div>
    );
};

export default Approvals;
